import React, { memo, useCallback, useEffect, useMemo } from 'react';

import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';

import { DemographicInformationProvisionType } from 'constants/loanApplication';

import { pagesPropTypes } from '../../../MainContent.constants';

import { getConfig } from './Demographic.config';
import {
  checkAreRaceCheckboxesRequired,
  customOnChangeDemographicInformationProvisionType,
  customOnChangeEthnicityFields,
  customOnChangeEthnicityOrSexFields,
  customOnChangeRaceInput,
  normalizeForm,
} from './Demographic.utils';

const Demographic = ({
  changedFields,
  isLoading,
  isSaving,
  form,
  validationErrors,
  validateField,
  handleChange,
  entityId,
  setEntityConfig,
  setForm,
  setInForm,
  setValidationErrors,
  staticValidateField,
  entityData,
  pendingChanges,
  disabledPendingChangesHighlight,
  setIsTouched,
}) => {
  const sectionForm = form[entityId]?.declarations?.demographicInformation ?? {};

  const {
    ethnicity = {},
    race = {},
    demographicInformationProvisionType,
    isSexInfoNotProvidedDuringFaceToFaceInterview,
    sex,
  } = sectionForm;

  const { isOtherHispanicOrLatinoEthnicity } = ethnicity.hispanicOrLatinoOptions ?? {};
  const { isEthnicityNotProvidedDuringFaceToFaceInterview, isHispanicOrLatino } = ethnicity;

  const {
    isAmericanIndianOrAlaskaNative,
    isAsian,
    asianOptions = {},
    isNativeHawaiianOrOtherPacificIslander,
    isBlackOrAfricanAmerican,
    nativeHawaiianOrOtherPacificIslanderOptions = {},
    isWhite,
    isRaceNotProvided,
    isRaceNotProvidedDuringFaceToFaceInterview,
  } = race;

  const { isOtherAsian } = asianOptions;
  const { isOtherPacificIslander } = nativeHawaiianOrOtherPacificIslanderOptions;

  const isFaceToFaceInterview =
    demographicInformationProvisionType ===
    DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW;

  const isSexSelected = !!sex;

  const isEthnicityRadioGroupSelected =
    !!ethnicity.isHispanicOrLatino ||
    !!ethnicity.isNonHispanicOrLatino ||
    !!ethnicity.isEthnicityNotProvided;

  const isSomeOfEthnicityFieldsSelected =
    isEthnicityRadioGroupSelected || !!isEthnicityNotProvidedDuringFaceToFaceInterview;

  const areRaceCheckboxesRequired = useMemo(
    () =>
      checkAreRaceCheckboxesRequired({
        isAmericanIndianOrAlaskaNative,
        isAsian,
        isBlackOrAfricanAmerican,
        isNativeHawaiianOrOtherPacificIslander,
        isWhite,
        isRaceNotProvided,
        isRaceNotProvidedDuringFaceToFaceInterview,
        isFaceToFaceInterview,
      }),
    [
      isAmericanIndianOrAlaskaNative,
      isAsian,
      isBlackOrAfricanAmerican,
      isNativeHawaiianOrOtherPacificIslander,
      isWhite,
      isRaceNotProvided,
      isRaceNotProvidedDuringFaceToFaceInterview,
      isFaceToFaceInterview,
    ],
  );

  const onChangeRaceInput = useCallback(
    (e, value) => {
      customOnChangeRaceInput({
        e,
        value,
        setForm,
        entityId,
        setInForm,
        setValidationErrors,
      });
    },
    [entityId, setForm, setInForm, setValidationErrors],
  );

  const onChangeEthnicityFields = useCallback(
    (e, value, flagName) =>
      customOnChangeEthnicityFields({
        e,
        value,
        setInForm,
        entityId,
        setForm,
        staticValidateField,
        setIsTouched,
        flagName,
      }),
    [setInForm, entityId, setForm, staticValidateField, setIsTouched],
  );

  const onChangeDemographicInformationProvisionType = useCallback(
    (e, value) =>
      customOnChangeDemographicInformationProvisionType({
        e,
        value,
        setInForm,
        entityId,
        setForm,
        validateField,
      }),
    [setInForm, entityId, setForm, validateField],
  );

  const onChangeSexFields = useCallback(
    (e, value) =>
      customOnChangeEthnicityOrSexFields({
        e,
        value,
        setInForm,
        entityId,
        setForm,
        staticValidateField,
      }),
    [setInForm, entityId, setForm, staticValidateField],
  );

  const config = useMemo(
    () =>
      getConfig({
        entityId,
        isHispanicOrLatino,
        isOtherHispanicOrLatinoEthnicity,
        isAmericanIndianOrAlaskaNative,
        isAsian,
        isOtherAsian,
        isNativeHawaiianOrOtherPacificIslander,
        isOtherPacificIslander,
        areRaceCheckboxesRequired,
        onChangeRaceInput,
        isFaceToFaceInterview,
        isSexInfoNotProvidedDuringFaceToFaceInterview,
        isEthnicityNotProvidedDuringFaceToFaceInterview,
        isSexSelected,
        isEthnicityRadioGroupSelected,
        isSomeOfEthnicityFieldsSelected,
        onChangeSexFields,
        onChangeEthnicityFields,
        onChangeDemographicInformationProvisionType,
      }),
    [
      entityId,
      isHispanicOrLatino,
      isOtherHispanicOrLatinoEthnicity,
      isAmericanIndianOrAlaskaNative,
      isAsian,
      isOtherAsian,
      isNativeHawaiianOrOtherPacificIslander,
      isOtherPacificIslander,
      areRaceCheckboxesRequired,
      onChangeRaceInput,
      isFaceToFaceInterview,
      isSexInfoNotProvidedDuringFaceToFaceInterview,
      isEthnicityNotProvidedDuringFaceToFaceInterview,
      isSexSelected,
      isEthnicityRadioGroupSelected,
      isSomeOfEthnicityFieldsSelected,
      onChangeSexFields,
      onChangeEthnicityFields,
      onChangeDemographicInformationProvisionType,
    ],
  );

  useEffect(() => {
    setForm(form =>
      normalizeForm({
        form,
        entityId,
        setValidationErrors,
        entityData,
      }),
    );
  }, [config, entityId, setForm, setValidationErrors, entityData]);

  useEffect(() => {
    if (!config) {
      return;
    }

    setEntityConfig(config);
  }, [config, setEntityConfig]);

  return (
    <FormGenerator
      isNestedForm
      changedFields={changedFields}
      config={config}
      disabledPendingChangesHighlight={disabledPendingChangesHighlight}
      form={form}
      formName={FormNames.DECLARATIONS}
      handleChange={handleChange}
      isLoading={isSaving || isLoading}
      numberOfColumns={1}
      pendingChanges={pendingChanges}
      setInForm={setInForm}
      validateField={validateField}
      validationErrors={validationErrors}
    />
  );
};

Demographic.propTypes = pagesPropTypes;

export default memo(Demographic);
